import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/UI';
import type { EntryScorecardsRow } from '@/types/appRouter';
import { isHasBacktestsCheck } from '../utils/systemChecks';

type CheckStatus = 'passed' | 'failed' | 'warning' | 'not_applicable';
type CheckCategory = 'configuration' | 'testing' | 'deployment';

interface SystemCheck {
    id: string;
    name: string;
    description: string;
    category: CheckCategory;
    status: CheckStatus;
    details: string | null;
    link: string | null;
}

type CheckLinkState = { strategyId: number };

type CheckLinkConfig = {
    to: string;
    label: string;
    state?: CheckLinkState;
};

interface Props {
    checks: SystemCheck[];
    strategyId: number;
    activeScorecard: EntryScorecardsRow | null;
}

const categoryLabels: Record<CheckCategory, string> = {
    configuration: 'Configuration',
    testing: 'Testing',
    deployment: 'Deployment'
};

interface StatusConfigItem {
    bg: string;
    text: string;
    icon: React.ReactNode;
}

const getStatusConfig = (status: CheckStatus): StatusConfigItem => {
    switch (status) {
        case 'passed':
            return {
                bg: 'bg-green-900/20',
                text: 'text-green-400',
                icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                )
            };
        case 'failed':
            return {
                bg: 'bg-red-900/20',
                text: 'text-red-400',
                icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                )
            };
        case 'warning':
            return {
                bg: 'bg-yellow-900/20',
                text: 'text-yellow-400',
                icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                        />
                    </svg>
                )
            };
        case 'not_applicable':
            return {
                bg: 'bg-zinc-800',
                text: 'text-zinc-500',
                icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M20 12H4" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                )
            };
    }
};

const linkLabelForStatus = (status: CheckStatus) => (status === 'passed' ? 'View' : 'Fix');

const toBacktestStatus = (strategyId: number) => `/user/tradingStrategy/${strategyId}?section=backtests`;

const withStrategyIdQuery = (to: string, strategyId: number) => {
    const [path, query = ''] = to.split('?');
    const params = new URLSearchParams(query);
    params.set('strategyId', String(strategyId));
    return `${path}?${params.toString()}`;
};

const getLinkConfigFromExplicitLink = (check: SystemCheck, strategyId: number): CheckLinkConfig | null => {
    if (!check.link) return null;

    if (check.link.startsWith('/tools/backtestStatus')) {
        return { to: toBacktestStatus(strategyId), state: { strategyId }, label: linkLabelForStatus(check.status) };
    }

    if (check.link === '/user/experiments' || check.link.startsWith('/user/experiments?')) {
        return { to: withStrategyIdQuery(check.link, strategyId), label: linkLabelForStatus(check.status) };
    }

    return { to: check.link, label: linkLabelForStatus(check.status) };
};

const getLinkConfigFromBacktests = (normalizedName: string, status: CheckStatus, strategyId: number): CheckLinkConfig | null => {
    if (!normalizedName.includes('backtest')) return null;

    return { to: toBacktestStatus(strategyId), state: { strategyId }, label: linkLabelForStatus(status) };
};

const getLinkConfigFromScorecards = (
    normalizedName: string,
    activeScorecard: EntryScorecardsRow | null,
    strategyId: number
): CheckLinkConfig | null => {
    if (!normalizedName.includes('scorecard')) return null;

    if (activeScorecard?.id) {
        const qs = `scope=strategy&strategyId=${strategyId}`;
        return { to: `/user/scorecard/${activeScorecard.id}?${qs}`, label: 'View' };
    }

    return { to: `/user/scorecards?scope=strategy&strategyId=${strategyId}`, label: 'Manage' };
};

const getLinkConfigFromSimpleMappings = (normalizedName: string, strategyId: number): CheckLinkConfig | null => {
    const mappings: { test: (name: string) => boolean; to: string; label: string }[] = [
        { test: (name) => name.includes('trading account'), to: '/user/tradingAccounts', label: 'Manage' },
        {
            test: (name) => name.includes('third party') || name.includes('broker') || name.includes('connection'),
            to: '/user/thirdPartyConnections',
            label: 'Manage'
        },
        {
            test: (name) => name.includes('trade logic v2 entry') || name.includes('entry configured'),
            to: '/user/tradingStrategies',
            label: 'Manage'
        },
        {
            test: (name) => name.includes('trade logic v2 exit') || name.includes('exit configured'),
            to: '/user/tradingStrategies',
            label: 'Manage'
        },
        { test: (name) => name.includes('trading profile'), to: '/user/tradingProfiles', label: 'Manage' },
        { test: (name) => name.includes('portfolio'), to: '/user/portfolios', label: 'Manage' },
        { test: (name) => name.includes('entity list'), to: '/user/entityLists', label: 'Manage' },
        { test: (name) => name.includes('real time') || name.includes('realtime'), to: '/user/realTimeEntries', label: 'View' },
        { test: (name) => name.includes('entry log'), to: '/user/entryLog', label: 'View' },
        { test: (name) => name.includes('experiment'), to: `/user/experiments?strategyId=${strategyId}`, label: 'View' }
    ];

    for (const mapping of mappings) {
        if (mapping.test(normalizedName)) {
            return { to: mapping.to, label: mapping.label };
        }
    }

    return null;
};

const getLinkConfigForCheck = (
    check: SystemCheck,
    strategyId: number,
    activeScorecard: EntryScorecardsRow | null
): CheckLinkConfig | null => {
    if (isHasBacktestsCheck(check)) {
        return { to: toBacktestStatus(strategyId), state: { strategyId }, label: linkLabelForStatus(check.status) };
    }

    const explicit = getLinkConfigFromExplicitLink(check, strategyId);
    if (explicit) return explicit;

    const normalizedName = check.name.toLowerCase();

    const backtests = getLinkConfigFromBacktests(normalizedName, check.status, strategyId);
    if (backtests) return backtests;

    const scorecards = getLinkConfigFromScorecards(normalizedName, activeScorecard, strategyId);
    if (scorecards) return scorecards;

    return getLinkConfigFromSimpleMappings(normalizedName, strategyId);
};

const SystemChecksSection: React.FC<Props> = ({ checks, strategyId, activeScorecard }) => {
    const [expandedCategories, setExpandedCategories] = useState<Set<CheckCategory>>(new Set(['configuration', 'testing', 'deployment']));

    const toggleCategory = (category: CheckCategory) => {
        const newSet = new Set(expandedCategories);

        if (newSet.has(category)) {
            newSet.delete(category);
        } else {
            newSet.add(category);
        }

        setExpandedCategories(newSet);
    };

    const groupedChecks = useMemo(
        () =>
            checks.reduce(
                (acc, check) => {
                    if (!acc[check.category]) {
                        acc[check.category] = [];
                    }

                    acc[check.category].push(check);
                    return acc;
                },
                {} as Record<CheckCategory, SystemCheck[]>
            ),
        [checks]
    );

    const categories: CheckCategory[] = ['configuration', 'testing', 'deployment'];

    return (
        <Card subtitle="Auto-detected from existing data" title="System Checks">
            <div className="space-y-4">
                {categories.map((category) => {
                    const categoryChecks = groupedChecks[category] || [];
                    if (categoryChecks.length === 0) return null;

                    const isExpanded = expandedCategories.has(category);
                    const passedCount = categoryChecks.filter((c) => c.status === 'passed').length;

                    return (
                        <div key={category}>
                            <button
                                className="w-full flex items-center justify-between p-2 bg-zinc-800 rounded hover:bg-zinc-700 transition-colors"
                                onClick={() => toggleCategory(category)}
                            >
                                <span className="font-medium text-zinc-200">{categoryLabels[category]}</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-zinc-400">
                                        {passedCount}/{categoryChecks.length} passed
                                    </span>
                                    <svg
                                        className={`w-5 h-5 text-zinc-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                                    </svg>
                                </div>
                            </button>

                            {isExpanded && (
                                <div className="mt-2 space-y-2">
                                    {categoryChecks.map((check) => {
                                        const config = getStatusConfig(check.status);
                                        const linkConfig = getLinkConfigForCheck(check, strategyId, activeScorecard);

                                        return (
                                            <div className={`p-3 rounded ${config.bg} flex items-start gap-3`} key={check.id}>
                                                <div className={config.text}>{config.icon}</div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        {linkConfig ? (
                                                            <Link
                                                                className="font-medium text-blue-400 hover:text-blue-300 hover:underline"
                                                                state={linkConfig.state}
                                                                to={linkConfig.to}
                                                            >
                                                                {check.name}
                                                            </Link>
                                                        ) : (
                                                            <span className="font-medium text-zinc-200">{check.name}</span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-zinc-400 mt-0.5">{check.description}</p>
                                                    {check.details && <p className="text-xs text-zinc-500 mt-1">{check.details}</p>}
                                                </div>
                                                {linkConfig && check.status !== 'passed' && (
                                                    <Link
                                                        className="text-blue-400 hover:text-blue-300 text-sm shrink-0"
                                                        state={linkConfig.state}
                                                        to={linkConfig.to}
                                                    >
                                                        {linkConfig.label}
                                                    </Link>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

export default SystemChecksSection;
